import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import { Button, ButtonText } from "ui/gluestack/button";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { ReusableModal } from "ui/molecules/modal/ReusableModal";
import { ResidentBedModel } from "app/services/api/types/locations/models/ResidentBedModel";
import { BedRequest } from "app/services/api/types/locations/requests/BedRequest";

export interface EditBedModalRef {
  show: (bed?: ResidentBedModel) => void;
  hide: () => void;
}

type BedFormData = Omit<BedRequest, "room_id">;

interface EditBedModalProps {
  onSave: (bedData: BedFormData, bedId?: number) => void;
  isLoading?: boolean;
}

export const EditBedModal = forwardRef<EditBedModalRef, EditBedModalProps>(
  ({ onSave, isLoading = false }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const [bed, setBed] = useState<ResidentBedModel | undefined>();

    const {
      control,
      handleSubmit,
      reset,
      formState: { isValid },
    } = useForm<BedFormData>({
      defaultValues: {
        bed_number: "",
        description: "",
      },
      mode: "onChange",
    });

    useImperativeHandle(
      ref,
      () => ({
        show: (showBed?: ResidentBedModel) => {
          setBed(showBed);
          setIsVisible(true);
        },
        hide: () => {
          setIsVisible(false);
        },
      }),
      [],
    );

    useEffect(() => {
      if (isVisible) {
        if (bed) {
          reset({
            bed_number: bed.bed_number,
            description: bed.description,
          });
        } else {
          reset({
            bed_number: "",
            description: "",
          });
        }
      }
    }, [isVisible, bed, reset]);

    const handleSave = (data: BedFormData) => {
      onSave(data, bed?.id);
    };

    const handleClose = () => {
      reset();
      setIsVisible(false);
    };

    const canSave = isValid && !isLoading;

    return (
      <ReusableModal
        isVisible={isVisible}
        onClose={handleClose}
        title={bed ? "Edit Bed" : "Add Bed"}
        hasCancelButton
      >
        <View className="space-y-4">
          <FormControlledInput
            id="bed-number"
            name="bed_number"
            label="Bed Number"
            placeholder="e.g., A1, B2"
            control={control}
            isRequired
          />

          <FormControlledInput
            id="bed-description"
            name="description"
            label="Description"
            placeholder="Optional description for the bed"
            control={control}
            isRequired={false}
          />
        </View>

        <View className="flex-row gap-3 mt-6">
          <Button
            variant="outline"
            onPress={handleClose}
            className="flex-1"
            disabled={isLoading}
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            onPress={handleSubmit(handleSave)}
            className="flex-1"
            disabled={!canSave}
          >
            <ButtonText>
              {isLoading ? "Saving..." : bed ? "Update" : "Add"}
            </ButtonText>
          </Button>
        </View>
      </ReusableModal>
    );
  },
);

EditBedModal.displayName = "EditBedModal";
